import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Send2 } from '@/components/icons';
import { Button } from '@/components/ui/button';
import Loading from '@/components/ui/Loading';
import WrapperLayout from '@/pages/components/WrapperLayout';
import {
    createSection,
    getCustomSurveyQuestionElementId,
    getCustomSurveySectionElementId,
} from './builderUtils';
import { BuilderCanvas } from './components/BuilderCanvas';
import { BuilderFloatingToolbar } from './components/BuilderFloatingToolbar';
import { BuilderMobileAddBar } from './components/BuilderMobileAddBar';
import { BuilderTopBar } from './components/BuilderTopBar';
import { PublishReadinessPanel } from './components/PublishReadinessPanel';
import { useCustomSurveyBuilder } from './useCustomSurveyBuilder';

type PendingScrollTarget = { type: 'question' } | { type: 'section'; sectionId: string };

export default function CustomSurveyBuilderPage() {
    const { surveyId } = useParams();
    const builder = useCustomSurveyBuilder(surveyId);
    const pendingScrollTargetRef = useRef<PendingScrollTarget | null>(null);

    useEffect(() => {
        const target = pendingScrollTargetRef.current;
        if (!target) return;

        const elementId =
            target.type === 'section'
                ? getCustomSurveySectionElementId(target.sectionId)
                : builder.state.activeQuestionId
                  ? getCustomSurveyQuestionElementId(builder.state.activeQuestionId)
                  : null;

        if (!elementId) return;

        pendingScrollTargetRef.current = null;
        requestAnimationFrame(() => {
            document.getElementById(elementId)?.scrollIntoView({
                behavior: 'smooth',
                block: 'start',
            });
        });
    }, [builder.state.activeQuestionId, builder.state.snapshot.sections]);

    if (builder.isLoading) return <Loading />;

    if (!builder.customSurvey) {
        return (
            <WrapperLayout
                showHeader={false}
                width="full"
                title="Editor de formulário"
                subtitle="Crie formulários clínicos personalizados para envio aos seus pacientes."
                leftIcon={Send2}
            >
                <div className="mx-auto max-w-xl rounded-3xl border border-border bg-card p-8 text-center shadow-sm">
                    <h2 className="text-xl font-semibold text-foreground">
                        Formulário não encontrado
                    </h2>
                    <p className="mt-2 text-sm text-muted-foreground">
                        O formulário pode ter sido excluído ou você não tem acesso a ele.
                    </p>
                    <Button type="button" className="mt-6" onClick={builder.requestBack}>
                        Voltar para formulários
                    </Button>
                </div>
            </WrapperLayout>
        );
    }

    const addQuestionToSection = (sectionId: string) => {
        const sectionExists = builder.state.snapshot.sections.some(
            (section) => section.id === sectionId,
        );
        if (!sectionExists) return;

        pendingScrollTargetRef.current = { type: 'question' };
        builder.dispatch({
            type: 'addQuestion',
            sectionId,
        });
    };

    const addQuestionToActiveSection = () => {
        addQuestionToSection(builder.activeSectionId);
    };

    const addSection = () => {
        const section = createSection();

        pendingScrollTargetRef.current = {
            type: 'section',
            sectionId: section.id,
        };
        builder.dispatch({
            type: 'addSection',
            afterSectionId: builder.activeSectionId,
            section,
        });
    };

    return (
        <WrapperLayout
            showHeader={false}
            width="full"
            title="Editor de formulário"
            subtitle="Crie formulários clínicos personalizados para envio aos seus pacientes."
            leftIcon={Send2}
        >
            <BuilderTopBar
                canDiscardDraft={builder.canDiscardDraft}
                isBusy={builder.isBusy}
                saveStatus={builder.saveStatus}
                onBack={builder.requestBack}
                onDiscardDraft={builder.requestDiscardDraft}
                onImportGoogleForms={builder.requestImportGoogleForms}
                onPublish={builder.requestPublish}
                onRetrySave={builder.retrySave}
            />

            {builder.showPublishIssues ? (
                <PublishReadinessPanel
                    issues={builder.publishIssues}
                    onClose={builder.dismissPublishIssues}
                />
            ) : null}

            <div className="relative mx-auto max-w-5xl">
                <BuilderCanvas
                    activeQuestionId={builder.state.activeQuestionId}
                    dispatch={builder.dispatch}
                    onAddQuestion={addQuestionToSection}
                    onDeleteSection={builder.requestDeleteSection}
                    snapshot={builder.state.snapshot}
                />
                <BuilderFloatingToolbar
                    disabled={builder.isBusy}
                    onAddQuestion={addQuestionToActiveSection}
                    onAddSection={addSection}
                />
            </div>

            <BuilderMobileAddBar
                disabled={builder.isBusy}
                onAddQuestion={addQuestionToActiveSection}
                onAddSection={addSection}
            />
        </WrapperLayout>
    );
}
